/**
 * Warehouse Management React Query Hooks
 * 
 * Provides React Query hooks for warehouse operations including:
 * - Fetching warehouses with filtering
 * - Creating, updating, and deleting warehouses
 * - Warehouse configuration management
 * 
 * All hooks use React Query for automatic caching, background refetching,
 * and optimistic updates with proper cache invalidation.
 * 
 * @module hooks/useWarehouses
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import { warehouseService } from '../services/warehouseService';
import { API_ENDPOINTS } from '../config/api.config.js';

/**
 * Fetches warehouses with optional filtering
 * 
 * @param {Object} filters - Query parameters for filtering warehouses
 * @param {string} [filters.status] - Filter by warehouse status
 * @param {string} [filters.region] - Filter by region
 * @param {string} [filters.search] - Search by name or code
 * @returns {Object} React Query result with warehouses data
 * @property {Object} data - Warehouses response object
 * @property {Array} data.items - Array of warehouse records
 * @property {number} data.total - Total number of warehouses
 * @property {boolean} isLoading - Loading state
 * @property {boolean} isError - Error state
 */
export const useWarehouses = (filters = {}) => {
  return useQuery({
    queryKey: ['warehouses', filters],
    queryFn: async () => {
      try {
        const result = await warehouseService.getWarehouses(filters);
        return result || [];
      } catch {
        return [];
      }
    },
    retry: false,
  });
};

/**
 * Fetches a single warehouse by ID
 * 
 * Query is disabled if warehouseId is not provided
 * 
 * @param {string} warehouseId - Warehouse ID
 * @returns {Object} React Query result with warehouse data
 * @property {Object} data - Warehouse record with full details
 * @property {boolean} isLoading - Loading state
 * @property {boolean} isError - Error state
 */
export const useWarehouse = (warehouseId) => {
  return useQuery({
    queryKey: ['warehouses', warehouseId],
    queryFn: async () => {
      try {
        const result = await warehouseService.getWarehouseById(warehouseId);
        return result || null;
      } catch {
        return null;
      }
    },
    enabled: !!warehouseId,
    retry: false,
  });
};

/**
 * Mutation hook to create a new warehouse
 * 
 * Automatically invalidates warehouses query on success
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {boolean} isPending - Mutation pending state
 * 
 * @example
 * const createWarehouse = useCreateWarehouse();
 * createWarehouse.mutate({ 
 *   name: 'Main Warehouse', 
 *   code: 'WH-001',
 *   address: {...} 
 * });
 */
export const useCreateWarehouse = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: warehouseService.createWarehouse,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['warehouses'] });
    },
  });
};

/**
 * Mutation hook to update an existing warehouse
 * 
 * Automatically invalidates both the specific warehouse and warehouses list on success
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {boolean} isPending - Mutation pending state
 * 
 * @example
 * const updateWarehouse = useUpdateWarehouse();
 * updateWarehouse.mutate({ 
 *   warehouseId: '123', 
 *   data: { status: 'active' } 
 * });
 */
export const useUpdateWarehouse = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ warehouseId, data }) => warehouseService.updateWarehouse(warehouseId, data),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['warehouses', variables.warehouseId] });
      queryClient.invalidateQueries({ queryKey: ['warehouses'] });
    },
  });
};

/**
 * Mutation hook to delete a warehouse
 * 
 * Automatically invalidates warehouses query on success
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {boolean} isPending - Mutation pending state
 * 
 * @example
 * const deleteWarehouse = useDeleteWarehouse();
 * deleteWarehouse.mutate('warehouse_123');
 */
export const useDeleteWarehouse = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: warehouseService.deleteWarehouse,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['warehouses'] });
    },
  });
};

/**
 * Hook to get warehouse statistics
 * @returns {Object} Query result with warehouse statistics
 */
export const useWarehouseStats = () => {
  return useQuery({
    queryKey: ['warehouses', 'stats'],
    queryFn: async () => {
      try {
        const { data } = await api.get(`${API_ENDPOINTS.WAREHOUSES}/stats`);
        return data || {};
      } catch {
        
        return {};
      }
    },
    retry: false,
  });
};

/**
 * Hook to get warehouse dashboard data
 * @returns {Object} Query result with warehouse dashboard data
 */
export const useWarehouseDashboard = () => {
  return useQuery({
    queryKey: ['warehouses', 'dashboard'],
    queryFn: async () => {
      try {
        const { data } = await api.get(`${API_ENDPOINTS.WAREHOUSES}/dashboard`);
        return data || {};
      } catch {
        
        return {};
      }
    },
    refetchInterval: 30000,
    retry: false,
  });
};

/**
 * Hook to get warehouse performance data
 * @returns {Object} Query result with warehouse performance data
 */
export const useWarehousePerformance = () => {
  return useQuery({
    queryKey: ['warehouses', 'performance'],
    queryFn: async () => {
      try {
        const { data } = await api.get(`${API_ENDPOINTS.WAREHOUSES}/performance`);
        return data || {};
      } catch {
        
        return {};
      }
    },
    refetchInterval: 30000,
    retry: false,
  });
};

/**
 * Mutation hook to simulate warehouse failover
 * 
 * Automatically invalidates warehouse dashboard query on success
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {boolean} isPending - Mutation pending state
 */
export const useSimulateWarehouseFailover = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (warehouseId) => {
      const response = await api.post(
        `${API_ENDPOINTS.WAREHOUSES}/simulate/failover/${warehouseId}`,
        {},
        { timeout: 10000 }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['warehouses', 'dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['warehouses', 'performance'] });
    },
  });
};

/**
 * Hook to get warehouse metrics
 * @param {string} timeRange - Time range filter (e.g., 'week', 'month')
 * @returns {Object} Query result with warehouse metrics data
 */
export const useWarehouseMetrics = (timeRange = 'week') => {
  return useQuery({
    queryKey: ['warehouses', 'metrics', timeRange],
    queryFn: async () => {
      try {
        const { data } = await api.get(`${API_ENDPOINTS.WAREHOUSES}/warehouses/metrics?time_range=${timeRange}`);
        const metrics = data || {};
        return Array.isArray(metrics.data) ? metrics.data : (Array.isArray(metrics) ? metrics : []);
      } catch {
        
        return [];
      }
    },
    retry: false,
  });
};

/**
 * Hook to get warehouse regions data
 * @param {string} timeRange - Time range filter (e.g., 'week', 'month')
 * @returns {Object} Query result with warehouse regions data
 */
export const useWarehouseRegions = (timeRange = 'week') => {
  return useQuery({
    queryKey: ['warehouses', 'regions', timeRange],
    queryFn: async () => {
      try {
        const { data } = await api.get(`${API_ENDPOINTS.WAREHOUSES}/warehouses/regions?time_range=${timeRange}`);
        const regions = data || {};
        return Array.isArray(regions.data) ? regions.data : (Array.isArray(regions) ? regions : []);
      } catch {
        
        return [];
      }
    },
    retry: false,
  });
};

/**
 * Query hook to fetch warehouse 3D elements
 * 
 * @param {string} warehouseId - Warehouse ID
 * @returns {Object} React Query result with warehouse elements data
 * @property {Array} data - Array of warehouse elements
 * @property {boolean} isLoading - Loading state
 */
export const useWarehouseElements = (warehouseId) => {
  return useQuery({
    queryKey: ['warehouses', warehouseId, 'elements'],
    queryFn: async () => {
      try {
        const headers = {
          'Authorization': `Bearer ${localStorage.getItem('wms_access_token')}`,
          'Content-Type': 'application/json'
        };
        const { data } = await api.get(`${API_ENDPOINTS.WAREHOUSES}/${warehouseId}/elements`, { headers });
        return data || [];
      } catch {
        
        return [];
      }
    },
    enabled: !!warehouseId,
    retry: false,
  });
};

/**
 * Query hook to fetch warehouse 3D layers
 * 
 * @param {string} warehouseId - Warehouse ID
 * @returns {Object} React Query result with warehouse layers data
 * @property {Array} data - Array of warehouse layers
 * @property {boolean} isLoading - Loading state
 */
export const useWarehouseLayers = (warehouseId) => {
  return useQuery({
    queryKey: ['warehouses', warehouseId, 'layers'],
    queryFn: async () => {
      try {
        const headers = {
          'Authorization': `Bearer ${localStorage.getItem('wms_access_token')}`,
          'Content-Type': 'application/json'
        };
        const { data } = await api.get(`${API_ENDPOINTS.WAREHOUSES}/${warehouseId}/layers`, { headers });
        return data || [];
      } catch {
        
        return [];
      }
    },
    enabled: !!warehouseId,
    retry: false,
  });
};

/**
 * Query hook to fetch warehouse performance data
 * 
 * @returns {Object} React Query result with warehouse performance data
 * @property {Object} data - Warehouse performance data object
 * @property {boolean} isLoading - Loading state
 */
export const useWarehousePerformanceData = () => {
  return useQuery({
    queryKey: ['warehouses', 'performance-data'],
    queryFn: async () => {
      try {
        const { data } = await api.get(`${API_ENDPOINTS.ANALYTICS}/warehouses/performance`);
        return data || [];
      } catch {
        // Return empty array if endpoint doesn't exist (404) or other errors
        
        return [];
      }
    },
    retry: false, // Don't retry on 404 errors
  });
};

/**
 * Query hook to fetch warehouse timeline data
 * 
 * @param {string} warehouseId - Warehouse ID (optional - if not provided, fetches global timeline)
 * @returns {Object} React Query result with warehouse timeline data
 * @property {Array} data - Warehouse timeline events array
 * @property {boolean} isLoading - Loading state
 */
export const useWarehouseTimeline = (warehouseId) => {
  return useQuery({
    queryKey: ['warehouses', warehouseId || 'global', 'timeline'],
    queryFn: async () => {
      try {
        const url = warehouseId 
          ? `${API_ENDPOINTS.WAREHOUSES}/${warehouseId}/timeline`
          : `${API_ENDPOINTS.WAREHOUSES}/timeline`;
        const { data } = await api.get(url);
        return data || [];
      } catch {
        // Return empty array if endpoint doesn't exist (404) or other errors
        
        return [];
      }
    },
    retry: false, // Don't retry on 404 errors
  });
};

/**
 * Query hook to fetch warehouse distribution data
 * 
 * @returns {Object} React Query result with warehouse distribution data
 * @property {Array} data - Warehouse distribution array
 * @property {boolean} isLoading - Loading state
 */
export const useWarehouseDistribution = () => {
  return useQuery({
    queryKey: ['warehouses', 'distribution'],
    queryFn: async () => {
      try {
        const { data } = await api.get(`${API_ENDPOINTS.ANALYTICS}/warehouses/distribution`);
        return data || { typeDistribution: [] };
      } catch {
        // Return empty distribution if endpoint doesn't exist (404) or other errors
        
        return { typeDistribution: [] };
      }
    },
    retry: false, // Don't retry on 404 errors
  });
};

/**
 * Query hook to fetch warehouse recommendations
 * 
 * @returns {Object} React Query result with warehouse recommendations
 * @property {Array} data - Warehouse recommendations array
 * @property {boolean} isLoading - Loading state
 */
export const useWarehouseRecommendations = () => {
  return useQuery({
    queryKey: ['warehouses', 'recommendations'],
    queryFn: async () => {
      try {
        const { data } = await api.get(`${API_ENDPOINTS.ANALYTICS}/warehouses/recommendations`);
        return data || [];
      } catch {
        // Return empty array if endpoint doesn't exist (404) or other errors
        
        return [];
      }
    },
    retry: false, // Don't retry on 404 errors
  });
};

/**
 * Query hook to fetch warehouse billing history
 * 
 * @returns {Object} React Query result with billing history data
 * @property {Object} data - Billing history data object
 * @property {boolean} isLoading - Loading state
 */
export const useWarehouseBillingHistory = () => {
  return useQuery({
    queryKey: ['warehouses', 'billing-history'],
    queryFn: async () => {
      try {
        const { data } = await api.get(`${API_ENDPOINTS.WAREHOUSES}/billing-history`);
        return data || {};
      } catch {
        
        return {};
      }
    },
    retry: false,
  });
};

/**
 * Query hook to fetch warehouse routing rules
 * 
 * @returns {Object} React Query result with routing rules data
 * @property {Object} data - Routing rules data object
 * @property {boolean} isLoading - Loading state
 */
export const useWarehouseRoutingRules = () => {
  return useQuery({
    queryKey: ['warehouses', 'routing-rules'],
    queryFn: async () => {
      try {
        const { data } = await api.get(`${API_ENDPOINTS.WAREHOUSES}/routing-rules`);
        return data || {};
      } catch {
        
        return {};
      }
    },
    retry: false,
  });
};

/**
 * Mutation hook to update warehouse 3D elements and layers
 * 
 * @param {string} warehouseId - Warehouse ID
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {Function} mutateAsync - Async version of mutate
 * @property {boolean} isPending - Mutation pending state
 */
export const useUpdateWarehouse3D = (warehouseId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ elements, layers }) => {
      const response = await api.put(
        `${API_ENDPOINTS.WAREHOUSES}/${warehouseId}`,
        { elements, layers }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['warehouses', warehouseId, 'elements'] });
      queryClient.invalidateQueries({ queryKey: ['warehouses', warehouseId, 'layers'] });
      queryClient.invalidateQueries({ queryKey: ['warehouses', warehouseId] });
    },
  });
};

/**
 * Query hook to fetch warehouse live status
 * 
 * @param {string} warehouseId - Warehouse ID
 * @returns {Object} React Query result with live status data
 * @property {Object} data - Live status data object
 * @property {boolean} isLoading - Loading state
 */
export const useWarehouseLiveStatus = (warehouseId) => {
  return useQuery({
    queryKey: ['warehouses', warehouseId, 'live-status'],
    queryFn: async () => {
      try {
        const response = await api.get(`${API_ENDPOINTS.WAREHOUSES}/${warehouseId}/live-status`);
        if (response.status >= 200 && response.status < 300) {
          const liveData = response?.data || {};
          return liveData;
        }
        return {};
      } catch {
        
        return {};
      }
    },
    enabled: !!warehouseId,
    refetchInterval: 5000,
    retry: false,
  });
};

/**
 * Query hook to fetch warehouse zones
 * 
 * Fetches zones for a specific warehouse.
 * 
 * @param {string} warehouseId - Warehouse ID
 * @returns {Object} React Query result with zones data
 * @property {Array} data - Array of zone records
 * @property {boolean} isLoading - Loading state
 * @property {boolean} isError - Error state
 */
export const useWarehouseZones = (warehouseId) => {
  return useQuery({
    queryKey: ['warehouses', warehouseId, 'zones'],
    queryFn: async () => {
      try {
        const response = await api.get(`${API_ENDPOINTS.INVENTORY}/warehouses/${warehouseId}/zones`);
        const data = response.data || {};
        return Array.isArray(data) ? data : (Array.isArray(data.zones) ? data.zones : []);
      } catch {
        
        return [];
      }
    },
    enabled: !!warehouseId,
    retry: false,
  });
};

/**
 * Query hook to fetch warehouse equipment
 * 
 * Fetches equipment for a specific warehouse.
 * 
 * @param {string} warehouseId - Warehouse ID
 * @returns {Object} React Query result with equipment data
 * @property {Array} data - Array of equipment records
 * @property {boolean} isLoading - Loading state
 * @property {boolean} isError - Error state
 */
export const useWarehouseEquipment = (warehouseId) => {
  return useQuery({
    queryKey: ['warehouses', warehouseId, 'equipment'],
    queryFn: async () => {
      try {
        const response = await api.get(`${API_ENDPOINTS.INVENTORY}/warehouses/${warehouseId}/equipment`);
        const data = response.data || {};
        return Array.isArray(data) ? data : (Array.isArray(data.equipment) ? data.equipment : []);
      } catch {
        
        return [];
      }
    },
    enabled: !!warehouseId,
    retry: false,
  });
};

/**
 * Query hook to fetch warehouse movements
 * 
 * Fetches movements for a specific warehouse.
 * 
 * @param {string} warehouseId - Warehouse ID
 * @returns {Object} React Query result with movements data
 * @property {Array} data - Array of movement records
 * @property {boolean} isLoading - Loading state
 * @property {boolean} isError - Error state
 */
export const useWarehouseMovements = (warehouseId) => {
  return useQuery({
    queryKey: ['warehouses', warehouseId, 'movements'],
    queryFn: async () => {
      try {
        const response = await api.get(`${API_ENDPOINTS.STOCK_MOVEMENTS}?warehouse_id=${warehouseId}`);
        const data = response.data || {};
        return Array.isArray(data) ? data : (Array.isArray(data.movements) ? data.movements : (Array.isArray(data.items) ? data.items : []));
      } catch {
        
        return [];
      }
    },
    enabled: !!warehouseId,
    retry: false,
  });
};

// ============================================================================
// CAD LAYOUT EDITOR HOOKS
// ============================================================================

/**
 * Query hook to fetch full warehouse layout for CAD editor
 * 
 * Fetches complete layout including racks, mezzanines, infrastructure elements
 * 
 * @param {string} warehouseId - Warehouse ID
 * @returns {Object} React Query result with full layout data
 */
export const useWarehouseFullLayout = (warehouseId) => {
  return useQuery({
    queryKey: ['warehouse-layout', warehouseId, 'full'],
    queryFn: async () => {
      // Try operations endpoint first (API Gateway)
      try {
        const response = await api.get(`/api/v1/operations/warehouse-layout/${warehouseId}`);
        return response.data || { zones: [], racks: [], mezzanines: [], infrastructure: [] };
      } catch {
        // Try WMS service direct endpoint
        const response = await api.get(`/api/v1/warehouse-layout/${warehouseId}/full-layout`);
        return response.data || { zones: [], racks: [], mezzanines: [], infrastructure: [] };
      }
    },
    enabled: !!warehouseId,
    staleTime: 30000, // Consider fresh for 30s
  });
};

/**
 * Mutation hook to save warehouse layout from CAD editor
 * 
 * Saves complete layout including all elements
 * 
 * @param {string} warehouseId - Warehouse ID
 * @returns {Object} React Query mutation result
 */
export const useSaveWarehouseLayout = (warehouseId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ elements, layers: _layers, dimensions: _dimensions }) => {
      if (!warehouseId) {
        throw new Error('Warehouse ID is required to save layout');
      }

      // Group elements by type
      const racks = elements.filter(e => e.type === 'rack' || e.type === 'shelving');
      const zones = elements.filter(e => e.type === 'zone');
      const infrastructure = elements.filter(e =>
        ['dock', 'workstation', 'conveyor', 'equipment', 'docks', 'elevator', 'stairs'].includes(e.type)
      );

      const results = { success: true, saved: [], failed: [] };
      const baseUrl = '/api/v1/warehouse-layout';

      // Save racks via batch endpoint
      if (racks.length > 0) {
        try {
          const rackData = racks.map(e => ({
            rack_id: e.id,
            rack_name: e.label,
            rack_type: e.subtype || 'selective',
            position_x: e.x / 20,
            position_y: 0,
            position_z: e.y / 20,
            rotation_y: e.rotation || 0,
            width: e.width / 20,
            depth: e.height / 20,
            total_height: e.metadata?.height || 6,
            num_levels: e.metadata?.levels || 4,
            num_bins_per_level: e.metadata?.bins || 2,
            upright_color: e.color || '#e65100',
            beam_color: '#1565c0'
          }));
          await api.post(`${baseUrl}/${warehouseId}/batch/racks`, { racks: rackData });
          results.saved.push(`${racks.length} racks`);
        } catch (err) {
          
          results.failed.push('racks');
        }
      }

      // Save zones via batch endpoint
      if (zones.length > 0) {
        try {
          const zoneData = zones.map(e => ({
            zone_id: e.id,
            zone_name: e.label,
            zone_type: e.subtype || 'storage',
            x: e.x / 20,
            y: 0,
            z: e.y / 20,
            width: e.width / 20,
            height: 3,
            depth: e.height / 20,
            color: e.color
          }));
          await api.post(`${baseUrl}/${warehouseId}/batch/zones`, { zones: zoneData });
          results.saved.push(`${zones.length} zones`);
        } catch (err) {
          
          results.failed.push('zones');
        }
      }

      // Save infrastructure via batch endpoint
      if (infrastructure.length > 0) {
        try {
          const infraData = infrastructure.map(e => ({
            element_id: e.id,
            element_type: e.type === 'dock' ? 'dock_door' : e.type,
            name: e.label,
            position_x: e.x / 20,
            position_y: 0,
            position_z: e.y / 20,
            rotation_y: e.rotation || 0,
            width: e.width / 20,
            height: e.metadata?.height || 4,
            depth: e.height / 20,
            config: e.metadata || {}
          }));
          await api.post(`${baseUrl}/${warehouseId}/batch/infrastructure`, { infrastructure: infraData });
          results.saved.push(`${infrastructure.length} infrastructure elements`);
        } catch (err) {
          
          results.failed.push('infrastructure');
        }
      }

      // If all backend saves failed, throw error instead of localStorage fallback
      if (results.failed.length > 0 && results.saved.length === 0) {
        throw new Error(`Failed to save warehouse layout: ${results.failed.join(', ')}`);
      }

      results.message = results.saved.length > 0
        ? `Saved: ${results.saved.join(', ')}`
        : 'No elements to save';
      return results;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['warehouse-layout', warehouseId] });
      queryClient.invalidateQueries({ queryKey: ['warehouses', warehouseId] });
    },
  });
};

/**
 * Query hook to fetch layout templates
 * 
 * @returns {Object} React Query result with templates
 */
export const useLayoutTemplates = () => {
  return useQuery({
    queryKey: ['warehouse-layout', 'templates'],
    queryFn: async () => {
      try {
        const response = await api.get('/api/v1/warehouse-layout/templates');
        return response.data || [];
      } catch {
        
        return [];
      }
    },
    retry: false,
  });
};

/**
 * Mutation hook to save layout as template
 * 
 * @returns {Object} React Query mutation result
 */
export const useSaveLayoutTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ name, description, layoutData, isPublic }) => {
      const response = await api.post('/api/v1/warehouse-layout/templates', {
        name,
        description,
        layout_data: layoutData,
        is_public: isPublic || false
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['warehouse-layout', 'templates'] });
    },
  });
};

/**
 * Mutation hook to apply template to warehouse
 * 
 * @param {string} warehouseId - Warehouse ID
 * @returns {Object} React Query mutation result
 */
export const useApplyLayoutTemplate = (warehouseId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ templateId, clearExisting = false }) => {
      const response = await api.post(
        `/api/v1/warehouse-layout/${warehouseId}/apply-template/${templateId}`,
        null,
        { params: { clear_existing: clearExisting } }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['warehouse-layout', warehouseId] });
      queryClient.invalidateQueries({ queryKey: ['warehouses', warehouseId] });
    },
  });
};

